import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Clock, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const GSTRegistration = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    pan: '',
    businessType: '',
    address: '',
    annualTurnover: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappMessage = `*GST Registration Request*%0A%0A*Business Name:* ${formData.businessName}%0A*Owner Name:* ${formData.ownerName}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone}%0A*PAN:* ${formData.pan}%0A*Business Type:* ${formData.businessType}%0A*Address:* ${formData.address}%0A*Annual Turnover:* ${formData.annualTurnover}%0A*Message:* ${formData.message}`;
    window.open(`https://wa.me/916268642514?text=${whatsappMessage}`, '_blank');
  };

  const requirements = [
    'PAN Card of Business/Proprietor',
    'Aadhaar Card',
    'Business Address Proof',
    'Bank Account Statement',
    'Business Registration Certificate',
    'Photographs',
  ];

  const process = [
    { title: 'Document Collection', time: '1 Day', icon: <FileText size={24} /> },
    { title: 'Application Filing', time: '1 Day', icon: <Clock size={24} /> },
    { title: 'Verification', time: '2-3 Days', icon: <AlertCircle size={24} /> },
    { title: 'GST Number Issued', time: '3-7 Days', icon: <CheckCircle size={24} /> },
  ];

  return (
    <div className="pt-32 pb-20" data-testid="gst-registration-page">
      {/* Hero Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight" data-testid="gst-reg-title">
              GST Registration
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Complete assistance for GST registration. Get your GSTIN in 3-7 working days with our expert guidance.
            </p>
            <div className="inline-block bg-[#10B981] text-white px-8 py-3 rounded-full font-bold text-xl">
              Registration Fee: ₹3,000
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-12 text-center">Registration Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-[#2D3C92] hover:shadow-xl transition-all"
                data-testid={`process-step-${index}`}
              >
                <div className="bg-[#2D3C92] w-12 h-12 rounded-full flex items-center justify-center text-white mb-4 floating">
                  {step.icon}
                </div>
                <div className="text-sm text-[#10B981] font-bold mb-2">Step {index + 1}</div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{step.title}</h3>
                <p className="text-slate-600">Timeline: {step.time}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="requirements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Required Documents</h2>
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#10B981] flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-700">{req}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Why Choose Us?</h2>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-[#0F172A] mb-2">✓ Quick Processing</h4>
                  <p className="text-slate-600 text-sm">Get your GST registration in 3-7 working days</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-[#0F172A] mb-2">✓ Expert Assistance</h4>
                  <p className="text-slate-600 text-sm">Guidance from experienced tax professionals</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-[#0F172A] mb-2">✓ Affordable Pricing</h4>
                  <p className="text-slate-600 text-sm">Transparent pricing with no hidden charges</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 bg-white" data-testid="registration-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100"
          >
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">GST Registration Form</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="gst-reg-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="businessName">Business Name *</Label>
                  <Input
                    id="businessName"
                    required
                    placeholder="Enter business name"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ownerName">Owner Name *</Label>
                  <Input
                    id="ownerName"
                    required
                    placeholder="Enter owner name"
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    data-testid="owner-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    required
                    placeholder="10-digit mobile number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="pan">PAN Number *</Label>
                  <Input
                    id="pan"
                    required
                    placeholder="ABCDE1234F"
                    value={formData.pan}
                    onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                    data-testid="pan-input"
                  />
                </div>
                <div>
                  <Label htmlFor="businessType">Business Type *</Label>
                  <Input
                    id="businessType"
                    required
                    placeholder="Proprietor/Partnership/Company"
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    data-testid="business-type-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Business Address *</Label>
                <Textarea
                  id="address"
                  required
                  rows={3}
                  placeholder="Complete business address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="address-input"
                />
              </div>

              <div>
                <Label htmlFor="annualTurnover">Expected Annual Turnover *</Label>
                <Input
                  id="annualTurnover"
                  required
                  placeholder="₹ 40 lakhs (or above)"
                  value={formData.annualTurnover}
                  onChange={(e) => setFormData({ ...formData, annualTurnover: e.target.value })}
                  data-testid="turnover-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Additional Information</Label>
                <Textarea
                  id="message"
                  rows={3}
                  placeholder="Any specific requirements or queries"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="message-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#10B981] hover:bg-[#047857] py-6 text-lg"
                data-testid="submit-gst-form"
              >
                <Send size={20} className="mr-2" />
                Submit GST Registration Request
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GSTRegistration;